import React, { useEffect, useState } from 'react';

import AppConsent from 'files/AppConsent.js';
import Button from 'views/site/Button.js';
import Modal from 'views/site/Modal.js';
import Route from 'files/Route.js';
import Theme from 'styles/Theme.js';

/*
 * ConsentModal
 *
 * The embedded app's privacy prompt, drawn in this site's own chrome.
 *
 * It renders at the site root rather than inside the frame view, because
 * consent is a property of the visit and not of one page. Rendered inside the
 * component that hosts the iframe it would inherit that component's stacking
 * context and its lifetime - it would sit in the app's corner of the page and
 * vanish the moment somebody navigated away mid question.
 *
 * Nothing in here knows what consent means. It has no idea which regime is in
 * play, what "accept" does, or what the wording says. It receives already
 * translated strings, renders them, and reports which key was pressed. The
 * reasoning stays in one place, in the app.
 */
const ConsentModal = () => {

    const [request, setRequest] = useState(AppConsent.get());

    useEffect(() => {
        return AppConsent.subscribe(setRequest);
    }, []);

    if(!request) {
        return null;
    }

    /*
     * Reading the policy means leaving this page, which unmounts the frame the
     * app is running in. A modal left standing over a route that no longer
     * hosts the app is a question with nowhere to send its answer, so it closes
     * on the way out.
     *
     * Dismissing first, synchronously, matters: it is what posts the dismissal
     * into the frame, and a moment later the frame is gone. The app stops
     * waiting rather than holding a prompt open against a dead channel, and
     * asks again when the visitor comes back to it - an unanswered required
     * prompt is still unanswered, so nothing is lost by closing it here.
     */
    const onPrivacyPolicyPress = () => {
        AppConsent.respond(null);
        Route.push('privacy');
    };

    /*
     * The buttons live at the bottom of the BODY, not in `actions`. `actions`
     * is a header bar rendered before the title, and is meant for a modal that
     * has no title - passing both put the buttons to the left of the heading.
     *
     * Stacked and full width because one label is "Do Not Sell or Share My
     * Personal Information". Side by side in a 520pt dialog it either truncates
     * or forces the modal open, and truncating that sentence is not an option:
     * the wording is the compliance.
     *
     * The refusal is `outline`, not `secondary`. `secondary` paints solid near
     * black, which against a navy brand fill is the HEAVIER of the two - the
     * dialog was telling the eye that refusing was the main action. Outline is
     * lighter than the fill while staying a full width button with the full
     * sentence on it, so it is still one press and still conspicuous, which is
     * what the regime actually asks for. Making refusal harder than accepting
     * is the one thing a consent dialog must not do.
     */
    const getButtons = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,

            // separated by space rather than by a rule. the header already
            // carries one, and a second cut the dialog into three bands of
            // equal weight with nothing leading.
            marginTop: 26
        }}>
            {(request.buttons || []).map(button => (
                <Button
                key={button.key}
                label={button.label}
                onClick={() => AppConsent.respond(button.key)}
                style={{ width: '100%' }}
                type={button.style === 'primary' ? 'primary' : 'outline'} />
            ))}
        </div>
    );

    return (
        <Modal
        // a first prompt has no answer yet, so the buttons are the only way
        // out. reopened from settings a choice already exists, and closing
        // simply leaves it alone.
        dismissible={request.required !== true}
        label={request.title}
        onClose={() => AppConsent.respond(null)}
        open={true}
        subtitle={request.status || undefined}
        title={request.title}
        width={480}>
            <p style={{
                ...Theme.textStyles.body({ fontSize: 15, lineHeight: 1.55 }),
                margin: 0
            }}>{request.message}</p>

            {getButtons()}

            {/* Under the actions, and quiet. It is a reference rather than a
                third choice, and sitting above the buttons it occupied the
                place the eye goes looking for the primary action. */}
            {request.privacyPolicy && (
                <div style={{ marginTop: 18, textAlign: 'center' }}>
                    <button
                    onClick={onPrivacyPolicyPress}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: Theme.colors.subText(),
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        padding: 0,
                        textDecoration: 'underline'
                    }}>{request.privacyPolicy}</button>
                </div>
            )}
        </Modal>
    )
};

export default ConsentModal;
